import type { EnemyDef } from '../../content/enemies/enemies';
import type { Element } from '../game/elements';
import type { CombatState, PlayerState } from '../game/types';
import { rollSkillHit } from './accuracy';
import { affinityHint, computeModifiedSkillDamage } from './damage';
import { getStatus } from './statuses';

export interface SkillLike {
  id: string;
  name: string;
  element: Element | string;
  power: number;
  manaCost: number;
  accuracy?: number;
}

export type UseSkillResult =
  | { ok: false }
  | {
      ok: true;
      player: PlayerState;
      combat: CombatState;
      defeated: boolean;
      missed: boolean;
    };

/**
 * Player skill (acts first). Accuracy + luck; STR/INT + elemental + statuses.
 */
export function applyPlayerSkill(
  player: PlayerState,
  combat: CombatState,
  skill: SkillLike,
  enemy: Pick<EnemyDef, 'weak' | 'resist' | 'immune'>,
  rng: () => number = Math.random,
): UseSkillResult {
  if (!player.alive || combat.enemyHp <= 0 || player.mana < skill.manaCost) {
    return { ok: false };
  }

  const spent: PlayerState = {
    ...player,
    mana: player.mana - skill.manaCost,
  };

  const accuracy = skill.accuracy ?? 0.9;
  const hit = rollSkillHit(accuracy, player.stats.luck, rng);

  if (!hit) {
    return {
      ok: true,
      player: spent,
      combat: {
        ...combat,
        log: [`${skill.name} lệch mục tiêu!`, ...combat.log].slice(0, 8),
      },
      defeated: false,
      missed: true,
    };
  }

  const element = skill.element as Element;
  const dmg = computeModifiedSkillDamage(
    element,
    skill.power,
    player.stats,
    enemy.weak,
    enemy.resist,
    enemy.immune,
    combat.playerStatuses,
    combat.enemyStatuses,
  );
  const finalDamage = dmg.finalDamage;
  const powerUp = getStatus(combat.playerStatuses, 'power_up');

  const nextHp = Math.max(0, combat.enemyHp - finalDamage);
  const defeated = nextHp === 0;
  const result =
    `${skill.name} gây ${finalDamage} sát thương${affinityHint(dmg.affinity)}.` +
    (dmg.blockedByStatus ? ' Trạng thái miễn nhiễm đã chặn sát thương.' : '') +
    (defeated ? ' Kẻ địch đã bị hạ.' : '');

  // Consume power_up after a successful hit
  const playerStatuses = powerUp
    ? combat.playerStatuses.filter((s) => s.id !== 'power_up')
    : combat.playerStatuses;

  return {
    ok: true,
    player: spent,
    combat: {
      ...combat,
      enemyHp: nextHp,
      playerStatuses,
      log: [result, ...combat.log].slice(0, 8),
    },
    defeated,
    missed: false,
  };
}
